// pages/Club/ClubRegistrationPage.jsx — Verein registrieren (/register)
//
// Neu gefasst am 25.10.2026 (Befunde Vereinsanmeldung, Stufe 1):
//   - Gestaltung wie die öffentlichen Seiten (styles/ausschreibung.css, .au),
//     hell/dunkel, Schrittanzeige sichtbar, große Knöpfe, ab 390 px.
//     Vorher passten Klassen und CSS nicht zusammen (LIVE seit 28.04.2026
//     ungestaltet).
//   - EINE Passwortregel (lib/passwortRegel.js), als Liste zum Abhaken, geprüft
//     schon im Schritt „Zugang“. Vorher: Seite 6 Zeichen, Server 8 + Groß/Klein/Zahl.
//   - Fehler am Feld: Seite prüft mit lib/registrierungPruefen.js (Abschrift der
//     Serverprüfung), der Server antwortet mit { felder }; die Seite springt
//     zum Schritt und Feld des ersten Fehlers.
//   - Hausnummer freiwillig (Spalte seit 2026_10_25 NULL erlaubt), PLZ 4 oder 5 Ziffern.
//   - ?turnier=<id> (bzw. ?event=<id>) bleibt erhalten: nach der Registrierung
//     wird angemeldet und es geht direkt zur Anmeldung für dieses Turnier.
//   - Der Schritt „Dojosoftware“ ist entfallen: /api/dojo-sync/complete-registration
//     verlangt ein Token, ging also vor dem ersten Login nie. Verbinden geht
//     im Vereinsbereich unter „Dojosoftware“.
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import PublicNav from '../../components/PublicNav';
import { useThema, ThemaKnopf, OeffentlicherFuss } from '../../components/oeffentlich/ausschreibungTeile';
import PasswortRegeln from '../../components/passwort/PasswortRegeln';
import { passwortFehler } from '../../lib/passwortRegel';
import { pruefeRegistrierung } from '../../lib/registrierungPruefen';
import { vereinsAnmeldungSpeichern } from '../../lib/anmeldungSpeichern';
import { zielZusatz, zielNachVereinsLogin } from '../../lib/anmeldeZiel';
import '../../styles/ausschreibung.css';
import './ClubRegistrationPage.css';

const SCHRITTE = [
  { nr: 1, titel: 'Verein', felder: ['name', 'ansprechpartner', 'email', 'telefon', 'strasse', 'hausnummer', 'plz', 'ort', 'homepage'] },
  { nr: 2, titel: 'Zugang', felder: ['benutzername', 'passwort'] },
  { nr: 3, titel: 'Prüfen', felder: [] },
];
const schrittVon = (feld) => (SCHRITTE.find((s) => s.felder.includes(feld)) || SCHRITTE[0]).nr;

const LEER = {
  name: '', ansprechpartner: '', email: '', telefon: '',
  strasse: '', hausnummer: '', plz: '', ort: '', homepage: '',
  benutzername: '', passwort: '',
};

function Feld({ name, label, pflicht, hinweis, fehler, wert, onWert, typ = 'text', klasse = '', ...rest }) {
  const hid = hinweis ? `rg-h-${name}` : null;
  const fid = fehler ? `rg-f-${name}` : null;
  return (
    <div className={`rg-feld ${klasse} ${fehler ? 'rg-mit-fehler' : ''}`}>
      <label htmlFor={`rg-${name}`}>
        {label}{pflicht ? <span className="rg-pflicht"> *</span> : <span className="rg-frei"> (freiwillig)</span>}
      </label>
      <input
        id={`rg-${name}`} name={name} type={typ} value={wert} onChange={(e) => onWert(name, e.target.value)}
        aria-invalid={fehler ? 'true' : undefined}
        aria-describedby={[fid, hid].filter(Boolean).join(' ') || undefined}
        {...rest}
      />
      {fehler && <div className="rg-fehler" id={fid} role="alert"><span aria-hidden="true">!</span>{fehler}</div>}
      {hinweis && <div className="rg-hinweis" id={hid}>{hinweis}</div>}
    </div>
  );
}

export default function ClubRegistrationPage() {
  const navigate = useNavigate();
  const [suche] = useSearchParams();
  const turnierZiel = suche.get('turnier') || suche.get('turnierId');
  const eventZiel = suche.get('event');
  const { thema, wirksam, umschalten } = useThema();

  const [schritt, setSchritt] = useState(1);
  const [d, setD] = useState(LEER);
  const [felder, setFelder] = useState({});
  const [meldung, setMeldung] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [zeigen, setZeigen] = useState(false);
  const [fertig, setFertig] = useState(null);   // { name, benutzername } nach Erfolg ohne Auto-Login
  const [turnierName, setTurnierName] = useState('');
  const fokusNach = useRef(null);

  useEffect(() => { document.title = 'Verein registrieren · TDA Tournament Center'; }, []);

  // Name des Turniers für den Hinweis oben
  useEffect(() => {
    if (!turnierZiel) return undefined;
    let weg = false;
    fetch(`/api/turniere/public/${encodeURIComponent(turnierZiel)}/info`)
      .then((r) => (r.ok ? r.json() : null))
      .then((x) => { if (!weg && x?.data?.name) setTurnierName(x.data.name); })
      .catch(() => {});
    return () => { weg = true; };
  }, [turnierZiel]);

  // Nach einem Schrittwechsel zum ersten Fehlerfeld springen
  useEffect(() => {
    const feld = fokusNach.current;
    if (!feld) return;
    fokusNach.current = null;
    const el = document.getElementById(`rg-${feld}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.focus({ preventScroll: true });
    }
  }, [schritt, felder]);

  const setze = (name, wert) => {
    setD((alt) => ({ ...alt, [name]: wert }));
    // Fehler am Feld verschwindet, sobald es passt (Passwort live)
    if (felder[name]) {
      const neu = pruefeRegistrierung({ ...d, [name]: wert }, passwortFehler);
      setFelder((alt) => { const x = { ...alt }; if (neu[name]) x[name] = neu[name]; else delete x[name]; return x; });
    }
  };

  const fehlerZeigen = (alle) => {
    setFelder(alle);
    const erstes = Object.keys(alle).sort((a, b) => schrittVon(a) - schrittVon(b)
      || SCHRITTE[schrittVon(a) - 1].felder.indexOf(a) - SCHRITTE[schrittVon(b) - 1].felder.indexOf(b))[0];
    if (!erstes) return;
    fokusNach.current = erstes;
    setSchritt(schrittVon(erstes));
  };

  const weiter = () => {
    setMeldung('');
    const alle = pruefeRegistrierung(d, passwortFehler);
    const hier = {};
    for (const f of SCHRITTE[schritt - 1].felder) if (alle[f]) hier[f] = alle[f];
    if (Object.keys(hier).length) { fehlerZeigen(hier); return; }
    setFelder({});
    setSchritt((s) => Math.min(3, s + 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const zurueck = () => { setMeldung(''); setSchritt((s) => Math.max(1, s - 1)); };

  const absenden = async () => {
    setMeldung('');
    const alle = pruefeRegistrierung(d, passwortFehler);
    if (Object.keys(alle).length) { fehlerZeigen(alle); return; }
    setLaeuft(true);
    try {
      const r = await fetch('/api/auth/register', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(d),
      });
      const x = await r.json().catch(() => ({}));
      if (!r.ok || x.success === false) {
        if (x.felder && Object.keys(x.felder).length) {
          fehlerZeigen(x.felder);   // Meldung steht am Feld, nicht noch einmal unten
        } else if (r.status === 429) {
          setMeldung(x.error || 'Zu viele Versuche. Bitte später erneut versuchen.');
        } else {
          setMeldung(x.error || `Die Registrierung hat nicht geklappt (Fehler ${r.status}). Bitte erneut versuchen.`);
        }
        return;
      }
      // Gleich anmelden, damit es ohne Umweg weitergeht (Turnier-Bezug bleibt)
      const l = await fetch('/api/auth/login', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: d.benutzername.trim(), passwort: d.passwort }),
      });
      const ld = await l.json().catch(() => ({}));
      if (l.ok && ld.success && ld.token && !ld.zwei_faktor) {
        vereinsAnmeldungSpeichern(ld, d.email.trim());
        navigate(zielNachVereinsLogin({ turnier: turnierZiel, event: eventZiel }), { replace: true });
        return;
      }
      setFertig({ name: d.name.trim(), benutzername: d.benutzername.trim() });
    } catch {
      setMeldung('Keine Verbindung zum Server. Bitte erneut versuchen.');
    } finally {
      setLaeuft(false);
    }
  };

  const anmeldenLink = () => `/anmelden${zielZusatz({ turnier: turnierZiel, event: eventZiel, name: fertig?.benutzername })}`;
  const loginMitZiel = `/anmelden${zielZusatz({ turnier: turnierZiel, event: eventZiel })}`;

  const feld = (name, label, extra = {}) => (
    <Feld name={name} label={label} wert={d[name]} onWert={setze} fehler={felder[name]} {...extra} />
  );

  return (
    <div className="au" data-thema={thema || undefined}>
      <PublicNav />
      <main className="au-wrap rg">
        <div className="au-d-oben rg-oben">
          <div>
            <h1 className="rg-titel">Verein registrieren</h1>
            <p className="rg-unter">Kostenlos, in drei kurzen Schritten. Danach legst du deine Sportler an und meldest sie zu Turnieren an.</p>
          </div>
          <ThemaKnopf wirksam={wirksam} umschalten={umschalten} />
        </div>

        {(turnierZiel || eventZiel) && (
          <div className="rg-ziel" role="status">
            Danach geht es direkt zur Anmeldung{turnierName ? <> für <strong>{turnierName}</strong></> : ' für die gewählte Veranstaltung'}.
          </div>
        )}

        {fertig ? (
          <section className="au-abschnitt rg-karte rg-fertig">
            <h2>Geschafft, {fertig.name} ist registriert</h2>
            <p>Melde dich jetzt mit deinem Benutzernamen <strong>{fertig.benutzername}</strong> und deinem Passwort an.</p>
            <Link className="au-knopf au-haupt rg-gross" to={anmeldenLink()}>Jetzt anmelden</Link>
          </section>
        ) : (
          <>
            <ol className="rg-schritte" aria-label="Fortschritt">
              {SCHRITTE.map((s) => (
                <li key={s.nr} className={s.nr === schritt ? 'rg-aktiv' : s.nr < schritt ? 'rg-erledigt' : ''}
                  aria-current={s.nr === schritt ? 'step' : undefined}>
                  <span className="rg-nr">{s.nr < schritt ? '✓' : s.nr}</span>
                  <span className="rg-schritt-titel">{s.titel}</span>
                </li>
              ))}
            </ol>

            <section className="au-abschnitt rg-karte">
              {schritt === 1 && (
                <>
                  <h2>Dein Verein</h2>
                  <p className="rg-lead">Mit * markiert ist Pflicht, alles andere kannst du auch später im Profil ergänzen.</p>
                  <div className="rg-raster">
                    {feld('name', 'Name des Vereins', { pflicht: true, klasse: 'rg-breit', autoComplete: 'organization', placeholder: 'z. B. Karate-Dojo Musterstadt e. V.' })}
                    {feld('ansprechpartner', 'Ansprechperson', { klasse: 'rg-breit', autoComplete: 'name' })}
                    {feld('email', 'E-Mail', { pflicht: true, typ: 'email', autoComplete: 'email', inputMode: 'email', hinweis: 'Hierhin gehen Bestätigungen und Rechnungen. Du kannst dich auch damit anmelden.' })}
                    {feld('telefon', 'Telefon', { pflicht: true, typ: 'tel', autoComplete: 'tel', hinweis: 'Für Rückfragen des Veranstalters.' })}
                    {feld('strasse', 'Straße', { klasse: 'rg-strasse', autoComplete: 'address-line1' })}
                    {feld('hausnummer', 'Nr.', { klasse: 'rg-nr-feld', autoComplete: 'off' })}
                    {feld('plz', 'PLZ', { klasse: 'rg-plz', inputMode: 'numeric', autoComplete: 'postal-code', hinweis: '4 Ziffern (AT, CH) oder 5 (DE)' })}
                    {feld('ort', 'Ort', { klasse: 'rg-ort', autoComplete: 'address-level2' })}
                    {feld('homepage', 'Internetseite', { klasse: 'rg-breit', typ: 'url', inputMode: 'url', autoComplete: 'url', placeholder: 'https://' })}
                  </div>
                </>
              )}

              {schritt === 2 && (
                <>
                  <h2>Dein Zugang</h2>
                  <p className="rg-lead">Mit Benutzername (oder E-Mail) und Passwort meldest du dich später an.</p>
                  <div className="rg-raster">
                    {feld('benutzername', 'Benutzername', { pflicht: true, klasse: 'rg-breit', autoComplete: 'username', autoCapitalize: 'none', spellCheck: false, hinweis: 'Mindestens 3 Zeichen, z. B. der Kurzname deines Vereins.' })}
                    <div className={`rg-feld rg-breit ${felder.passwort ? 'rg-mit-fehler' : ''}`}>
                      <label htmlFor="rg-passwort">Passwort<span className="rg-pflicht"> *</span></label>
                      <div className="rg-pw">
                        <input id="rg-passwort" name="passwort" type={zeigen ? 'text' : 'password'} value={d.passwort}
                          onChange={(e) => setze('passwort', e.target.value)} autoComplete="new-password"
                          aria-invalid={felder.passwort ? 'true' : undefined} aria-describedby="rg-pw-regeln rg-f-passwort" />
                        <button type="button" className="rg-pw-zeigen" onClick={() => setZeigen((z) => !z)}
                          aria-label={zeigen ? 'Passwort verbergen' : 'Passwort anzeigen'}>{zeigen ? 'Verbergen' : 'Zeigen'}</button>
                      </div>
                      {felder.passwort && <div className="rg-fehler" id="rg-f-passwort" role="alert"><span aria-hidden="true">!</span>{felder.passwort}</div>}
                      <PasswortRegeln passwort={d.passwort} id="rg-pw-regeln" />
                    </div>
                  </div>
                </>
              )}

              {schritt === 3 && (
                <>
                  <h2>Alles richtig?</h2>
                  <dl className="rg-pruef">
                    <dt>Verein</dt><dd>{d.name}</dd>
                    {d.ansprechpartner && <><dt>Ansprechperson</dt><dd>{d.ansprechpartner}</dd></>}
                    <dt>E-Mail</dt><dd>{d.email}</dd>
                    <dt>Telefon</dt><dd>{d.telefon}</dd>
                    {(d.strasse || d.plz || d.ort) && (
                      <><dt>Anschrift</dt><dd>{[[d.strasse, d.hausnummer].filter(Boolean).join(' '), [d.plz, d.ort].filter(Boolean).join(' ')].filter(Boolean).join(', ')}</dd></>
                    )}
                    <dt>Benutzername</dt><dd>{d.benutzername}</dd>
                  </dl>
                  <button type="button" className="rg-link" onClick={() => setSchritt(1)}>Angaben ändern</button>
                  <p className="rg-klein">Nutzt dein Verein schon die Dojosoftware? Die Verbindung richtest du nach der Registrierung im Vereinsbereich unter „Dojosoftware“ ein, dann kommen deine Mitglieder als Sportler herüber.</p>
                </>
              )}

              {meldung && <div className="rg-meldung" role="alert">{meldung}</div>}

              <div className="rg-knoepfe">
                {schritt > 1 && <button type="button" className="au-knopf au-neben rg-gross" onClick={zurueck} disabled={laeuft}>Zurück</button>}
                {schritt < 3
                  ? <button type="button" className="au-knopf au-haupt rg-gross" onClick={weiter}>Weiter</button>
                  : <button type="button" className="au-knopf au-haupt rg-gross" onClick={absenden} disabled={laeuft}>{laeuft ? 'Wird angelegt…' : 'Verein registrieren'}</button>}
              </div>
            </section>

            <p className="rg-fuss">Schon ein Konto? <Link to={loginMitZiel}>Hier anmelden</Link></p>
          </>
        )}
      </main>
      <OeffentlicherFuss />
    </div>
  );
}
